import { ApiHelper } from "../../api/index";
import {
  ColumnLineage,
  Columns,
  DbtInfoResponse,
  ExposureMetaData,
  FunctionMetaData,
  Ref,
  SnowflakeStageMetadata,
  StaticLineage,
  Table,
} from "../types";
import { CllEvents } from "../cllUtils";

interface ColumnLineageResponse {
  column_lineage: ColumnLineage[];
  confidence?: { confidence: string; operator_list?: string[] };
  errors?: Record<string, string[]>;
}

interface LineageSettings {
  showSelectEdges: boolean;
  showNonSelectEdges: boolean;
  defaultExpansion: number;
  /** ERD overlay master toggle. Defaults to true when omitted. */
  showRefs?: boolean;
  /** Per-source ERD lens visibility. Object so adding sources is non-breaking. */
  enabledRefSources?: Partial<Record<"test" | "contract" | "semantic" | "inferred", boolean>>;
  /** Inferred-edge confidence floor, 0..1. Default 0.6. */
  inferenceConfidenceThreshold?: number;
  /** Whether to include sources/seeds as inference candidates. Default false. */
  includeSourcesInInference?: boolean;
}

export const upstreamTables = (table: string) => {
  return ApiHelper.get<{
    tables: Table[];
  }>("upstreamTables", { table });
};

export const downstreamTables = (table: string) => {
  return ApiHelper.get<{
    tables: Table[];
  }>("downstreamTables", { table });
};

export const getExposureDetails = (name: string) => {
  return ApiHelper.get<ExposureMetaData>("getExposureDetails", {
    name,
  });
};

export const getFunctionDetails = (name: string) => {
  return ApiHelper.get<FunctionMetaData>("getFunctionDetails", {
    name,
  });
};

export const getSnowflakeStageDetails = (table_rk: string) => {
  return ApiHelper.get<SnowflakeStageMetadata>("getSnowflakeStageDetails", {
    table_rk,
  });
};

export const getDbtInfo = (table_rk: string) => {
  return ApiHelper.get<DbtInfoResponse>("getDbtInfo", {
    table_rk,
  });
};

export const getColumns = (table: string, refresh: boolean) => {
  return ApiHelper.get<Columns>("getColumns", {
    table,
    refresh,
  });
};

export const getConnectedColumns = (body: {
  targets: [string, string][];
  upstreamExpansion: boolean;
  currAnd1HopTables: string[];
  selectedColumn: { name: string; table: string };
  showIndirectEdges: boolean;
}) => {
  return ApiHelper.get<ColumnLineageResponse>("getConnectedColumns", body);
};

export const sendFeedback = (body: { feedback_value: "good" | "bad"; feedback_text: string }) => {
  return ApiHelper.get<{ ok: boolean }>("sendFeedback", body);
};

export const getLineageSettings = () => {
  return ApiHelper.get<LineageSettings>("getLineageSettings", {});
};

export const persistLineageSettings = (body: Partial<LineageSettings>) => {
  return ApiHelper.get<{
    ok: boolean;
  }>("persistLineageSettings", body);
};

/**
 * ERD overlay: fetch PK/FK relationships derived from the manifest.
 * Returns an empty list on projects with no declared FK tests.
 */
export const getRelationships = () => {
  return ApiHelper.get<{ refs: Ref[] }>("getRelationships", {});
};

export const initLineage = () => {
  return ApiHelper.get("init", {});
};

export const openFile = (url: string) => {
  return ApiHelper.get("openFile", { url });
};

export const openChat = () => {
  return ApiHelper.get("openChat", {});
};

export const openAltimateCodeChat = (payload: {
  table: string;
  column?: string;
  level: "model" | "column";
  question: string;
}) => {
  return ApiHelper.get("openAltimateCodeChat", payload);
};

export const showInfoNotification = (message: string) => {
  return ApiHelper.get("showInfoNotification", { message });
};

export const previewFeature = () => {
  return ApiHelper.get("previewFeature", {});
};

export const columnLineage = (event: CllEvents) => {
  return ApiHelper.get("columnLineage", { event });
};

export const telemetryEvents = (data: Record<string, unknown>) => {
  return ApiHelper.get("telemetryEvents", data);
};

export const showExternalViewCodeModal = (data: Record<string, unknown>) => {
  return ApiHelper.get("showViewCodeModal", data);
};

export const exportLineage = (data: { name: string; lineage_data: StaticLineage }) => {
  return ApiHelper.post<{ url: string }>("dbt/v4/export-lineage", data);
};

export const finalLineageDownload = (data: { column_rk: string }): Promise<void> => {
  return ApiHelper.get<void>("finalColumnsDownload", data);
};

export const finalTableLineageDownload = (data: {
  table_rk: string;
  upstream: boolean;
  downstream: boolean;
}): Promise<void> => {
  return ApiHelper.get<void>("finalTablesDownload", data);
};
